import React, { useContext, useEffect, useMemo, useState } from 'react';
import { ResponsiveLine, Serie } from '@nivo/line';
import { Box, useTheme } from '@mui/material';
import { tokens } from '../theme';
import fetchData from '../helpers/fetchData';
import { useChartTheme } from '../themes/chartTheme';
import { FilterContext } from '../contexts/FilterContext';
import { dateToSqlDate } from '../helpers/dateFuncs';
import { determineAggregation } from '../helpers/chartUtils';
import LoadingSpinner from './LoadingSpinner';
import {
  useSourceDisplayConfig,
  getSourceColor,
  getDisplayName,
} from '../hooks/useSourceDisplayConfig';
import { SourceDisplayConfig } from '../types/leadSourceRules';

export interface LeadSourceData {
  source: string;
  date: string;
  count: number;
}

interface LeadSourceChartProps {
  selectedCustomer: string;
}

// Distinctive color mapping based on source volume analysis
// High-volume sources get more distinctive colors for better visibility
const sourceColorMap: Record<string, string> = {
  // FT top sources (traffic, sam, nl highest volume)
  traffic: '#2563eb', // Strong blue
  sam: '#8b5cf6', // Purple
  nl: '#10b981', // Green

  // BG top sources (aktiencheck dominant)
  aktiencheck: '#ef4444', // Red
  aktiencheck_video: '#f97316', // Orange-red
  adhoc: '#f59e0b', // Amber

  // SW top sources
  ai_articles: '#14b8a6', // Teal
  kme: '#ec4899', // Pink

  // Medium volume sources
  doi_zone: '#06b6d4', // Cyan
  pdf_analysis: '#6366f1', // Indigo
  push: '#a855f7', // Lighter purple

  // Lower volume sources - still distinctive
  sharedeals: '#84cc16', // Lime
  dnv: '#22c55e', // Light green
  other_articles: '#eab308', // Yellow
  newsletter: '#0ea5e9', // Sky blue

  // Fallback
  other: '#64748b', // Slate gray
};

const sourceDisplayNames: Record<string, string> = {
  traffic: 'Traffic',
  sam: 'SAM',
  nl: 'Newsletter',
  aktiencheck: 'Aktiencheck',
  aktiencheck_video: 'Aktiencheck Video',
  adhoc: 'Ad-hoc',
  ai_articles: 'AI Articles',
  kme: 'KME',
  doi_zone: 'DOI Zone',
  pdf_analysis: 'PDF Analysis',
  push: 'Push',
  sharedeals: 'Sharedeals',
  dnv: 'DNV',
  other_articles: 'Other Articles',
  newsletter: 'Newsletter',
  other: 'Other',
};

// Helper function to calculate ISO week number
const getISOWeek = (date: Date): number => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + 4 - (d.getDay() || 7));
  const yearStart = new Date(d.getFullYear(), 0, 1);
  return Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
};

// Helper function to format ISO date as "KW X, MMM" for display
const formatWeekForDisplay = (isoDateStr: string): string => {
  const date = new Date(isoDateStr);
  const weekNum = getISOWeek(date);
  const month = date.toLocaleDateString('de-DE', { month: 'short' });
  return `KW ${weekNum}, ${month}`;
};

const formatDateForAggregation = (dateStr: string, aggregation: string): string => {
  const date = new Date(dateStr);

  switch (aggregation) {
    case 'monthly':
      return date.toLocaleDateString('de-DE', {
        year: 'numeric',
        month: 'short',
      });
    case 'weekly':
      const weekStart = new Date(date);
      // getDay() returns 0 for Sunday — use 7 for ISO weeks (Mon=1, Sun=7)
      const dayOfWeek = date.getDay() || 7;
      weekStart.setDate(date.getDate() - dayOfWeek + 1);
      // Use local timezone components — toISOString() shifts dates at DST boundaries
      const y = weekStart.getFullYear();
      const m = String(weekStart.getMonth() + 1).padStart(2, '0');
      const d = String(weekStart.getDate()).padStart(2, '0');
      return `${y}-${m}-${d}`;
    case 'daily':
    default:
      return date.toLocaleDateString('de-DE', {
        day: '2-digit',
        month: '2-digit',
      });
  }
};

const aggregateData = (data: LeadSourceData[], aggregation: string): LeadSourceData[] => {
  const aggregatedMap = new Map<string, LeadSourceData>();

  data.forEach((item) => {
    const aggregatedDate = formatDateForAggregation(item.date, aggregation);
    const key = `${item.source}|${aggregatedDate}`;

    if (!aggregatedMap.has(key)) {
      aggregatedMap.set(key, {
        source: item.source,
        date: aggregatedDate,
        count: 0,
      });
    }

    const existing = aggregatedMap.get(key)!;
    existing.count += item.count;
  });

  return Array.from(aggregatedMap.values());
};

const processDataToSeries = (
  data: LeadSourceData[],
  configMap: Record<string, SourceDisplayConfig>,
): Serie[] => {
  // Group data by source
  const sourceData = new Map<string, Map<string, number>>();
  const allDates = new Set<string>();

  data.forEach((item) => {
    allDates.add(item.date);

    if (!sourceData.has(item.source)) {
      sourceData.set(item.source, new Map());
    }

    const sourceMap = sourceData.get(item.source)!;
    sourceMap.set(item.date, (sourceMap.get(item.date) || 0) + item.count);
  });

  // Sort dates chronologically
  const sortedDates = Array.from(allDates).sort((a, b) => {
    const dateA = new Date(a);
    const dateB = new Date(b);
    return dateA.getTime() - dateB.getTime();
  });

  // Fill missing dates with 0 for each source
  sourceData.forEach((dateMap, source) => {
    sortedDates.forEach((date) => {
      if (!dateMap.has(date)) {
        dateMap.set(date, 0);
      }
    });
  });

  // Convert to Nivo format, sorted by total volume (descending) for legend order
  const series: Serie[] = [];

  // Calculate total volume per source for sorting
  const sourceTotals = new Map<string, number>();
  sourceData.forEach((dateMap, source) => {
    const total = Array.from(dateMap.values()).reduce((sum, count) => sum + count, 0);
    sourceTotals.set(source, total);
  });

  // Sort sources by total volume descending
  const sortedSources = Array.from(sourceData.keys()).sort((a, b) => {
    return (sourceTotals.get(b) || 0) - (sourceTotals.get(a) || 0);
  });

  sortedSources.forEach((source) => {
    const dateMap = sourceData.get(source)!;
    const points = sortedDates.map((date) => ({
      x: date,
      y: dateMap.get(date) || 0,
    }));

    const cfg = configMap[source];
    series.push({
      id: cfg?.display_name || sourceDisplayNames[source] || source,
      data: points,
      color: cfg ? getSourceColor(cfg) : sourceColorMap[source] || '#64748b',
    });
  });

  return series;
};

const LeadSourceChart: React.FC<LeadSourceChartProps> = ({ selectedCustomer }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const chartTheme = useChartTheme();
  const { filterState } = useContext(FilterContext)!;
  const { configMap } = useSourceDisplayConfig(selectedCustomer);

  const [data, setData] = useState<LeadSourceData[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  const startDate = useMemo(() => filterState.startDate || new Date(), [filterState.startDate]);
  const endDate = useMemo(() => filterState.endDate || new Date(), [filterState.endDate]);

  useEffect(() => {
    const fetchSourceData = async () => {
      setLoading(true);
      try {
        const options = {
          start_date: dateToSqlDate(startDate),
          end_date: dateToSqlDate(endDate),
        };

        const result = await fetchData<LeadSourceData[]>(
          `/api/daily-controlling/source-data/${selectedCustomer}`,
          options,
        );

        const flatResult = Array.isArray(result) ? result.flat() : [];
        setData(flatResult);
      } catch (error) {
        console.error('Error fetching source data:', error);
        setData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchSourceData();
  }, [selectedCustomer, startDate, endDate]);

  const aggregation = useMemo(() => determineAggregation(startDate, endDate), [startDate, endDate]);

  const chartData = useMemo(() => {
    if (data.length === 0) return [];
    const aggregatedData = aggregateData(data, aggregation);
    return processDataToSeries(aggregatedData, configMap);
  }, [data, aggregation, configMap]);

  if (loading) {
    return <LoadingSpinner />;
  }

  if (chartData.length === 0) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100%"
        color={colors.grey[100]}
      >
        Keine Daten verfügbar
      </Box>
    );
  }

  return (
    <ResponsiveLine
      data={chartData}
      theme={chartTheme}
      colors={chartData.map((serie) => serie.color || '#64748b')}
      margin={{ top: 50, right: 180, bottom: 50, left: 60 }}
      xScale={{ type: 'point' }}
      yScale={{
        type: 'linear',
        min: 0,
        max: 'auto',
        stacked: false,
        reverse: false,
      }}
      curve="monotoneX"
      axisTop={null}
      axisRight={null}
      axisBottom={{
        tickSize: 5,
        tickPadding: 5,
        tickRotation: -45,
        legend: 'Datum',
        legendOffset: 45,
        legendPosition: 'middle',
        format: (value) => {
          // Convert ISO dates back to "KW X, MMM" format for weekly aggregation
          if (aggregation === 'weekly') {
            return formatWeekForDisplay(String(value));
          }
          return String(value);
        },
      }}
      axisLeft={{
        tickSize: 5,
        tickPadding: 5,
        tickRotation: 0,
        legend: 'Leads',
        legendOffset: -50,
        legendPosition: 'middle',
      }}
      enableGridX={false}
      enableGridY={true}
      pointSize={6}
      pointColor={{ theme: 'background' }}
      pointBorderWidth={2}
      pointBorderColor={{ from: 'serieColor' }}
      pointLabelYOffset={-12}
      useMesh={true}
      legends={[
        {
          anchor: 'right',
          direction: 'column',
          justify: false,
          translateX: 120,
          translateY: 0,
          itemsSpacing: 2,
          itemDirection: 'left-to-right',
          itemWidth: 100,
          itemHeight: 20,
          itemOpacity: 0.85,
          symbolSize: 12,
          symbolShape: 'circle',
          symbolBorderColor: 'rgba(0, 0, 0, .5)',
        },
      ]}
      tooltip={({ point }) => (
        <Box
          sx={{
            background: colors.primary[400],
            padding: '9px 12px',
            border: `1px solid ${colors.grey[700]}`,
            borderRadius: '4px',
          }}
        >
          <strong>{point.serieId}</strong>
          <br />
          {point.data.xFormatted}: {point.data.yFormatted} Leads
        </Box>
      )}
    />
  );
};

export default LeadSourceChart;
